import { useQuery } from "@tanstack/react-query";

import useApi from "./useApi";
import type { PlutoOutreachDraft } from "../components/pluto/outreach-draft";

export type PlutoConversationHistoryMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  toolNames: readonly string[];
  /** The drafts this turn delivered, or null for a turn that answered in prose. */
  draft: PlutoOutreachDraft | null;
  /** The user stopped this answer, so it stops mid-thought rather than concluding. */
  stopped: boolean;
  createdAt: string;
};

export type PlutoConversationHistory = {
  conversationId: string;
  title: string;
  messages: readonly PlutoConversationHistoryMessage[];
};

/**
 * Reads a stored conversation so a reload continues where the user left off.
 * Pass null to skip the request.
 *
 * The transcript is left stale on purpose: turns added after it was first read
 * live only in the client, so reopening the conversation has to ask the server
 * again instead of replaying an outdated cache entry.
 */
export const usePlutoConversationHistory = (conversationId: string | null) => {
  const { instance } = useApi();

  return useQuery<PlutoConversationHistory>({
    queryKey: ["pluto", "conversation-history", conversationId],
    queryFn: async ({ signal }) => {
      const response = await instance.get<PlutoConversationHistory>(
        `/v3/pluto/conversations/${String(conversationId)}/messages`,
        { signal },
      );
      return response.data;
    },
    enabled: conversationId !== null,
    retry: false,
    staleTime: 0,
  });
};

export default usePlutoConversationHistory;
